import React, { Component } from 'react';
import FaqSection from './FaqSection';
import pEntitiesVideo from './assets/video/web/pentities-demo.mp4';
import wf1 from './assets/workflow/wf1-designer.webp';
import wf2 from './assets/workflow/wf2-step-check.webp';
import wf3 from './assets/workflow/wf3-run-history.webp';
import wf4 from './assets/workflow/wf4-blueprint.webp';
import wf5 from './assets/workflow/wf5-advisor.webp';
import './pentities.css';
import './automation.css';
import NavBar from './NavBar';

interface AutomationProps {}

interface AutomationState {
  lightboxSrc: string | null;
  isVideoOpen: boolean;
  shareCopied: boolean;
}

class Automation extends Component<AutomationProps, AutomationState> {
  constructor(props: AutomationProps) {
    super(props);
    this.state = {
      lightboxSrc: null,
      isVideoOpen: false,
      shareCopied: false
    };
  }

  openVideo = () => {
    this.setState({ isVideoOpen: true });
  };

  closeVideo = () => {
    this.setState({ isVideoOpen: false });
  };

  shareVideo = async () => {
    const shareUrl = new URL(pEntitiesVideo, window.location.origin).href;
    const shareData = {
      title: 'pEntities See the platform in action',
      text: 'Check out how pEntities transforms the way businesses manage entities inside Microsoft 365.',
      url: shareUrl,
    };
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (_) {}
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        this.setState({ shareCopied: true });
        setTimeout(() => this.setState({ shareCopied: false }), 2500);
      } catch (_) {}
    }
  };

  openLightbox = (src: string) => {
    this.setState({ lightboxSrc: src });
  };

  closeLightbox = () => {
    this.setState({ lightboxSrc: null });
  };

  render() {
    return (
      <div className="pentities-page automation-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="pentities-hero-section">
          <div className="pentities-hero-video-background">
            <video
              preload="metadata"
              autoPlay
              loop
              muted
              playsInline
              className="pentities-hero-video"
            >
              <source src={require('./assets/video/web/14394920_1920_1080_24fps.mp4')} type="video/mp4" />
            </video>
          </div>
          <div className="pentities-container">
            <div className="pentities-hero-content">
              <span className="pentities-hero-eyebrow">Workflow Automation</span>
              <h1 className="pentities-hero-title">
                Describe the process. Get a running workflow.
              </h1>
              <p className="pentities-hero-description">
                Turn the processes that live in people's heads into automation that runs itself designed in a
                visual editor inside Microsoft Teams, on your own Microsoft 365 data, in your own tenant.
                No new tools, no developers, nothing leaving your control.
              </p>
              <div className="pentities-hero-cta">
                <a
                  href="https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pentities-hero-cta-btn"
                >
                  Book a Demo
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* General Description Section */}
        <section id="overview" className="pentities-gendesc-section">
          <div className="pentities-container">
            <div className="pentities-gendesc-content">
              <span className="pentities-gendesc-label">General Description</span>
              <h2 className="pentities-gendesc-title">Automation that understands the business</h2>
              <p>
                The contract that should be reviewed before it renews. The new joiner who needs six things to
                happen in order. The request that must reach the right team before its deadline. pEntities turns
                each of these into a process that runs itself, without anyone remembering to start it.
              </p>
              <p>
                <b>Trigger.</b> The event that starts the process a date, a change in a record, a new file, an
                incoming email. Defined once, it runs from then on.
              </p>
              <p>
                <b>Conditions.</b> The rules that decide which path the process takes, applied to live data at the
                moment it runs.
              </p>
              <p>
                <b>Actions.</b> What happens next produce a document, send an email, assign a task, notify a team,
                update a record, or reach an external system.
              </p>
              <p>
                <b>Full audit trail.</b> Every run is recorded: what ran, on what data, what came out and what went
                wrong. Available for review at any time.
              </p>
            </div>
          </div>
        </section>

        {/* The Workflow Engine dark theme */}
        <section id="engine" className="pentities-docmgmt-section">
          <div className="pentities-container">
            <span className="pentities-docmgmt-eyebrow">The Workflow Engine</span>
            <h2 className="pentities-docmgmt-title">One visual designer, inside Microsoft Teams</h2>
            <p className="pentities-docmgmt-lead">
              Build automation where the work already happens. No separate portal, no separate login, and nothing
              that reaches data the person who owns it couldn't reach themselves.
            </p>

            <div className="pentities-docmgmt-block">
              <h3>Design it by drawing it</h3>
              <figure className="pentities-docmgmt-figure is-float" onClick={() => this.openLightbox(wf1)}>
                <img loading="lazy" decoding="async" src={wf1} alt="The workflow designer inside Microsoft Teams" />
                <figcaption>The designer inside Teams the steps on the canvas, and the panel that configures them.</figcaption>
              </figure>
              <p>
                Processes are built by dragging steps onto a canvas and connecting them. Branches label themselves,
                repeating and simultaneous work is grouped visually, and a process that is used often can be saved
                once and reused inside others.
              </p>
              <p>
                What takes a specialist weeks in a traditional automation tool takes an informed business user an
                afternoon and the result is a diagram anyone in the room can read.
              </p>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>Nearly 40 ways a process can start</h3>
              <p>
                A workflow begins when something happens in the business, and pEntities is listening across the
                whole of Microsoft 365:
              </p>
              <ul>
                <li><b>Records &amp; documents</b> a record created or changed, a file added in SharePoint or OneDrive.</li>
                <li><b>Email, Teams &amp; calendar</b> a message matching your criteria, a channel post, an upcoming meeting.</li>
                <li><b>A schedule</b> once, or hourly, daily, weekly or monthly between two dates.</li>
                <li><b>Business events</b> leave submitted, training expiring, a contract renewing, a request nearing its deadline, a budget threshold crossed.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>Over 40 things it can do next</h3>
              <p>
                Workflows follow rules, repeat, run several streams at once and wait for people exactly as your
                real process does:
              </p>
              <ul>
                <li><b>Email &amp; documents</b> send and file correspondence, generate documents from templates, read and summarise attachments.</li>
                <li><b>Approvals that wait</b> a decision can wait days or weeks. Ask by email with your own buttons Renew, Renegotiate, Let it lapse and the process continues from the answer. No app, no login.</li>
                <li><b>Built-in intelligence</b> extract data from invoices and contracts, compare documents, spot patterns and flag exceptions.</li>
                <li><b>Your other systems</b> the workflow can call an external application or database when the process needs one.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Build, Verify & Trust white theme */}
        <section id="build" className="pentities-workflow-section">
          <div className="pentities-container">
            <span className="pentities-workflow-eyebrow">Build, Verify &amp; Trust</span>
            <h2 className="pentities-workflow-title">Workflows you describe in a sentence and can trust in production</h2>
            <p className="pentities-workflow-lead">
              Most automation projects stall before they start, because nobody is sure what to build. Blueprint
              removes that step: describe the problem in plain language and the workflows are designed, checked and
              placed on the canvas ready to review, rehearse and put live.
            </p>

            <div className="pentities-workflow-block automation-blueprint-block">
              <span className="automation-blueprint-badge">Blueprint · the AI workflow agent</span>
              <h3>Describe the problem in a sentence. Get the workflows that fix it.</h3>
              <figure className="pentities-workflow-figure is-float" onClick={() => this.openLightbox(wf4)}>
                <img loading="lazy" decoding="async" src={wf4} alt="Blueprint problem to recommended workflows" />
                <figcaption>Blueprint the problem in your own words, the diagnosis, and the workflows it recommends.</figcaption>
              </figure>
              <p className="automation-blueprint-quote">
                "Sales misses renewal dates and Legal is brought in too late."
              </p>
              <p>
                That one sentence is the whole brief. Blueprint reads it the way an experienced consultant would
                who is involved, where the process breaks, what it is costing and what should be measured then
                turns the diagnosis into working automation.
              </p>

              <ol className="automation-blueprint-steps">
                <li>
                  <b>You describe the problem</b> in the language you would use in a meeting. No process maps, no
                  requirements document, no technical detail.
                </li>
                <li>
                  <b>It diagnoses the process</b> the people involved, the steps that fail, the risk being carried
                  and the measures that would show improvement.
                </li>
                <li>
                  <b>It prescribes the workflows</b> a short list of concrete automations, each one explained in
                  business terms with the benefit it delivers.
                </li>
                <li>
                  <b>It builds them for you</b> the workflows you approve are created on the canvas, fully formed
                  and ready to review, adjust and put live. Nothing is left as a suggestion.
                </li>
              </ol>

              <p className="automation-blueprint-guardrail">
                <b>It can only propose what the platform can genuinely do.</b> Every recommendation is verified
                against the real catalogue of triggers and actions before you ever see it, so what appears on the
                canvas is always something that will actually run.
              </p>

              <figure className="pentities-workflow-figure is-float is-left" onClick={() => this.openLightbox(wf5)}>
                <img loading="lazy" decoding="async" src={wf5} alt="The Workflow Advisor beside the designer" />
                <figcaption>The Workflow Advisor suggests workflows from what it sees; any design is one click from being created.</figcaption>
              </figure>
              <p>
                Day to day, the <b>Workflow Advisor</b> continues the same conversation beside the designer. It
                reads the context you are working in, points out automations worth having, builds the one you
                describe in a sentence, and answers questions as they come up so the second and third workflow
                are as easy as the first.
              </p>
            </div>

            <div className="pentities-workflow-block">
              <h3>Check it, test it, watch it run</h3>
              <figure className="pentities-workflow-figure is-float" onClick={() => this.openLightbox(wf2)}>
                <img loading="lazy" decoding="async" src={wf2} alt="Step Check validation" />
                <figcaption>Every step is checked before the workflow goes live, so problems surface at design time.</figcaption>
              </figure>
              <p>
                Before a workflow is published, pEntities checks it end to end and points out anything that would
                fail: a step that can never be reached, a rule with no outcome, a loop with no stopping point.
              </p>
              <p>
                You can then rehearse it on real data without affecting anything, and see exactly what each step
                would do and which path it would take. Once live, every run is listed step by step with its status,
                duration and any errors updating as it happens.
              </p>
              <figure className="pentities-workflow-figure is-float is-left" onClick={() => this.openLightbox(wf3)}>
                <img loading="lazy" decoding="async" src={wf3} alt="Run history" />
                <figcaption>Run history every execution, step by step, with the detail of each one a click away.</figcaption>
              </figure>
              <p>
                Twenty ready-made workflows cover the processes most organisations start with invoice handling,
                employee onboarding, contract renewal reminders, request escalation so the first one is running
                the same day.
              </p>
            </div>

            <div className="pentities-workflow-block">
              <h3>Built for the real world and for your auditors</h3>
              <p>
                Workflows wait as long as a real process needs weeks for a signature, months for a renewal
                and recover from interruptions on their own, retrying a step that fails before escalating it. Safety
                limits stop a badly designed process from running away.
              </p>
              <p>
                Everything stays in your own Microsoft 365 and Azure tenant: workflows, run history and logs are
                stored separately per organisation and encrypted, and each workflow runs with the permissions of
                the person who owns it never reaching data that person could not open themselves.
              </p>
            </div>

          </div>
        </section>

        {/* Automation Promo Section */}
        <section id="automation-promo" className="automation-promo-section">
          <div className="automation-promo-inner">
            <div className="automation-promo-content">
              <h2>What if nothing ever slipped through the cracks again?</h2>
              <p>
                Approvals that route themselves. Deadlines that enforce themselves. Follow-ups that send themselves. Our automation platform runs inside Microsoft Teams no code, no third-party tools, no IT setup. Define the trigger, set the conditions, watch it execute. Every time. Precisely. Regardless of who's in the office.
              </p>
            </div>
            <div className="automation-promo-media">
              <div className="video-preview-block">
                <p className="video-preview-label">See the platform in action</p>
                <div className="video-preview-wrapper" onClick={this.openVideo} role="button" aria-label="Play pEntities video">
                  <video
                    className="video-preview-thumbnail"
                    src={`${pEntitiesVideo}#t=0.001`}
                    muted
                    playsInline
                    preload="metadata"
                  />
                  <div className="video-play-overlay">
                    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" className="video-play-icon">
                      <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.55)" />
                      <polygon points="24,16 52,32 24,48" fill="#ffffff" />
                    </svg>
                  </div>
                </div>
                <button
                  className="video-share-btn"
                  onClick={this.shareVideo}
                  aria-label="Share this video"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor" className="video-share-icon" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 14a2 2 0 00-1.32.5L7.4 10.7a2.1 2.1 0 000-1.4l6.28-3.8A2 2 0 1012.5 4l-6.28 3.8a2 2 0 100 4.4L12.5 16A2 2 0 1015 14z" />
                  </svg>
                  {this.state.shareCopied ? 'Link copied!' : 'Share this video'}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Video Modal */}
        {this.state.isVideoOpen && (
          <div className="video-modal-overlay" onClick={this.closeVideo} role="dialog" aria-modal="true" aria-label="pEntities video">
            <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="video-modal-close" onClick={this.closeVideo} aria-label="Close video">✕</button>
              <video
                className="video-modal-player"
                src={pEntitiesVideo}
                controls
                autoPlay
                playsInline
              />
            </div>
          </div>
        )}

        {/* Modules Section */}
        <section id="modules" className="pentities-modules-section">
          <div className="pentities-container">
            <span className="pentities-modules-eyebrow">pEntities Modules</span>
            <h2 className="pentities-modules-title">The same automation engine, wired into the work your teams actually do</h2>
            <p className="pentities-modules-lead">
              Workflow automation powers every pEntities module each with its own domain AI agent, all running
              inside Microsoft Teams on the data you already own.
            </p>

            <div className="pentities-modules-grid">
              <a href="/solutions/hr-template" className="pentities-module-card">
                <h3>HR Module</h3>
                <p>
                  The full employee lifecycle onboarding with automatic task lists, leave with multi-level
                  approval, performance reviews, training tracked to its expiry date and payroll analysis. The HR
                  agent spots compliance risks and writes the reports for you.
                </p>
                <span className="pentities-module-link">Explore HR →</span>
              </a>

              <a href="/solutions/case-management" className="pentities-module-card">
                <h3>Legal Module</h3>
                <p>
                  Every case tracked from pre-trial to appeal court, lawyers, opponents, confidentiality level,
                  financial exposure and risk. Contracts carry milestones, auto-renewal detection and signature
                  history, while the agent analyses timelines and exposure scenarios.
                </p>
                <span className="pentities-module-link">Explore Legal →</span>
              </a>

              <a href="/solutions/ticketing" className="pentities-module-card">
                <h3>Ticketing Module</h3>
                <p>
                  Every request handled with precision IT, HR, finance or legal. Real-time tracking, automatic
                  notifications, approval flows and full history, with priorities, SLAs and automation rules that
                  work without anyone intervening.
                </p>
                <span className="pentities-module-link">Explore Ticketing →</span>
              </a>

              <a href="/solutions/finance" className="pentities-module-card">
                <h3>Finance Module</h3>
                <p>
                  Invoices, expenses, purchases, budgets, receivables and tax data organised by fiscal year. Every
                  figure links back to its original email, document and approval task full traceability, and an
                  agent that spots trends and deviations.
                </p>
                <span className="pentities-module-link">Explore Finance →</span>
              </a>
            </div>
          </div>
        </section>

        {/* Image Lightbox */}
        {this.state.lightboxSrc && (
          <div className="pentities-lightbox" onClick={this.closeLightbox} role="dialog" aria-modal="true">
            <button className="pentities-lightbox-close" onClick={this.closeLightbox} aria-label="Close">✕</button>
            <img loading="lazy" decoding="async"
              className="pentities-lightbox-img"
              src={this.state.lightboxSrc}
              alt="Enlarged screenshot"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Footer */}
        <FaqSection path="/solutions/automation" />
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>

                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>

                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default Automation;
